import React, {
  useState,
  useRef,
  useLayoutEffect,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { Box } from 'theme-ui';
import { gql, useMutation } from '@apollo/client';

import styles from './Build.module.css';
import ToolbarNav from './ToolbarNav';
import RevertModal, { RevertModalType } from './RevertModal';
import { ReuseTemplateModal } from './ReuseTemplateModal';
import { QueryProvider } from 'query/QueryProvider';
import { EXTRACTION_STUDIES_LIST_ROUTE, tokenizeRoute } from 'query/routes';
import { captureApolloError } from 'util/apolloError';
import LoadingSpinner from 'system/elements/Spinner';
import FormattedDate from 'components/FormattedDate';
import { ID } from 'util/localTypes';
import { DE2SupportedSuggestion } from 'types/DataExtraction';

import { TemplateModel } from 'concepts/Extraction/TemplateModel';
import {
  fromModel,
  toModel,
} from 'concepts/Extraction/ViewModel/TemplateViewModel';
import BuildEditor from 'concepts/Extraction/FormBuilder/BuildEditor';
import { newBlankBlock } from 'concepts/Extraction/FormBuilder/newBlankBlock';
import BuildPreview from 'concepts/Extraction/Preview/BuildPreview';

import {
  CreateExtractionFormRevisionMutation,
  CompleteExtractionFormMutation,
  RevertTemplateMutation,
} from 'types/graphql';
import { ExtractionFormType } from 'concepts/Extraction/ExtractionFormType';
import BuildSettings from 'concepts/Extraction/FormBuilder/BuildSettings';
import { RevisitStudiesDialog } from 'concepts/Extraction/FormBuilder/RevisitStudiesDialog';
import { useConfirmUnsaved } from 'hooks/useConfirmUnsaved';
import { useReviewFeatures } from 'hooks/reviews/useReviewFeatures';
import {
  buildSuggestionFieldMatcher,
  buildSuggestionOptionsFinder,
} from 'concepts/AutoDocExtraction/suggestionFieldMatcher';
import {
  noSuggestionOptions,
  SuggestionAutocompleteProvider,
} from 'concepts/AutoDocExtraction/SuggestionAutocompleteContext';
import {
  Badge,
  Button,
  createToast,
  dismissToast,
  Heading,
  IconList,
  Toast,
} from 'components/core';
import { Inline } from 'components/layout';
import { TemplateToolInfo } from 'components/shared/ExtractionTemplateHeader/TemplateToolInfo';

export const CREATE_TEMPLATE_REVISION_MUTATION = gql`
  mutation CreateExtractionFormRevision(
    $extractionFormRevision: CreateExtractionFormRevisionInput!
  ) {
    createExtractionFormRevision(input: $extractionFormRevision) {
      success
      errors {
        message
      }
    }
  }
`;

export const COMPLETE_EXTRACTION_FORM_MUTATION = gql`
  mutation CompleteExtractionForm(
    $extractionFormRevision: CompleteExtractionFormInput!
  ) {
    completeExtractionForm(input: $extractionFormRevision) {
      success
      errors {
        message
      }
    }
  }
`;

export const REVERT_TEMPLATE_MUTATION = gql`
  mutation RevertTemplate($params: RevertTemplateInput!) {
    response: revertTemplate(input: $params) {
      success
      errors {
        message
      }
    }
  }
`;

const SAVE_FAILED = 'Your template could not be saved. Please try again.';
const PUBLISH_FAILED =
  'Your template could not be published. Please try again.';
// Reused so a retry replaces the previous failure
const FAILURE_TOAST_ID = 'extraction-build-failure';

export type BuildProps = {
  currentReviewerId: ID;
  reviewId: ID;
  formId?: ID;
  formType: ExtractionFormType;
  formSuperseded: boolean;
  formPublisher: {
    firstName: string;
    lastName: string;
    isCurrentReviewer: boolean;
    date: Date | null;
  };
  formEditor: {
    firstName: string;
    lastName: string;
    isCurrentReviewer: boolean;
    date: Date | null;
  };
  model: TemplateModel;
  backUrl: string;
  progress: {
    started: number;
    completed: number;
  };
  isRevertable: boolean;
  rawReviewId: string;
  studyId?: string;
  supportedSuggestions: DE2SupportedSuggestion[];
};

const LoadingOverlay = (): JSX.Element => (
  <div
    style={{
      position: 'absolute',
      height: '100%',
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgb(128, 128, 128, 0.5)',
      zIndex: 1,
    }}
  >
    <LoadingSpinner size="large" />
  </div>
);

export function useSyncedScrolling(): [
  (id: string) => void,
  React.MutableRefObject<HTMLDivElement | null>,
  React.MutableRefObject<HTMLDivElement | null>
] {
  const selectedBlockIDChange = useRef<boolean>(false);
  const selectedBlockID = useRef<string | null>(null);
  const editorPanel = useRef<HTMLDivElement | null>(null);
  const previewPanel = useRef<HTMLDivElement | null>(null);

  const selectFunction = useCallback((id: string) => {
    selectedBlockID.current = id;
    selectedBlockIDChange.current = true;
  }, []);

  useLayoutEffect(() => {
    if (selectedBlockIDChange.current) {
      selectedBlockIDChange.current = false;

      if (!selectedBlockID.current) {
        return;
      }

      const previewBlock = document.querySelector(
        `[data-preview-block-id="${selectedBlockID.current}"]`
      );
      const editorBlock = document.querySelector(
        `[data-block-id="${selectedBlockID.current}"]`
      );

      if (!previewBlock || !editorBlock) {
        return;
      }

      const offsetTopDiff =
        (editorBlock as HTMLElement).offsetTop -
        (previewBlock as HTMLElement).offsetTop;

      const scrollHandler = () => {
        if (
          editorPanel.current &&
          previewPanel.current &&
          previewBlock &&
          editorBlock &&
          previewPanel.current.scrollTo
        ) {
          previewPanel.current.scrollTo(
            0,
            editorPanel.current.scrollTop - offsetTopDiff
          );
        }
      };
      // Fire it once to bring the preview panel inline straight away
      scrollHandler();
      editorPanel.current?.addEventListener('scroll', scrollHandler);

      const editorPanelRef = editorPanel.current;
      return () => {
        editorPanelRef?.removeEventListener('scroll', scrollHandler);
      };
    }
  });

  return [selectFunction, editorPanel, previewPanel];
}

export default function Build(props: BuildProps): JSX.Element {
  const [viewModel, updateViewModel] = useState(() => {
    const initial = fromModel(props.model);
    // A saved draft can have no blocks at all
    return initial.blocks.length > 0
      ? initial
      : { blocks: [newBlankBlock(props.formType)] };
  });
  const {
    isLoading: featuresLoading,
    useSuggestions,
    hasFeature,
  } = useReviewFeatures();
  const isDataExtractionTemplate = props.formType === 'data_extraction';
  const suggestionsEnabled =
    !featuresLoading && useSuggestions && isDataExtractionTemplate;
  const isFieldSuggestable = useMemo(
    () =>
      suggestionsEnabled
        ? buildSuggestionFieldMatcher(props.supportedSuggestions, hasFeature)
        : () => false,
    [suggestionsEnabled, props.supportedSuggestions, hasFeature]
  );

  // A no-op finder rather than no provider while suggestions are off or the
  // review features are still loading: the label inputs stay the same
  // components either way, so nothing remounts (and loses focus) once the
  // features arrive
  const findSuggestionOptions = useMemo(
    () =>
      suggestionsEnabled
        ? buildSuggestionOptionsFinder(props.supportedSuggestions, hasFeature)
        : noSuggestionOptions,
    [suggestionsEnabled, props.supportedSuggestions, hasFeature]
  );

  const [selectedBlockID, updateSelectedBlockID] = useState<string | null>(
    null
  );
  const [showWarningDialog, updateShowWarningDialog] = useState<boolean>(false);

  const [
    createRevisionMutation,
    { error: createRevisionError, loading: saving },
  ] = useMutation<CreateExtractionFormRevisionMutation>(
    CREATE_TEMPLATE_REVISION_MUTATION
  );
  if (createRevisionError) captureApolloError(createRevisionError);

  const [
    completeRevisionMutation,
    { error: completeFormError, loading: completing },
  ] = useMutation<CompleteExtractionFormMutation>(
    COMPLETE_EXTRACTION_FORM_MUTATION
  );
  if (completeFormError) captureApolloError(completeFormError);

  const [
    revertTemplateMutation,
    { error: revertTemplateError, loading: reverting },
  ] = useMutation<RevertTemplateMutation>(REVERT_TEMPLATE_MUTATION);
  if (revertTemplateError) captureApolloError(revertTemplateError);

  const revertTemplateOnClick = () => {
    revertTemplateMutation({
      variables: {
        params: {
          templateId: props.formId,
        },
      },
    })
      .then(({ data }) => {
        const response = data?.response;
        if (response?.errors) {
          setShowRevertModal('error');
        } else {
          setShowRevertModal('success');
        }
      })
      .catch(() => setShowRevertModal('error'));
  };

  const [showReuseModal, setShowReuseModal] = useState(false);

  const [clean, dirty] = useConfirmUnsaved();
  const [published, setPublished] = useState(!props.formSuperseded);
  const [lastSaved, setLastSaved] = useState(props.formEditor.date);
  const displayEditorName = props.formEditor.isCurrentReviewer
    ? 'You'
    : props.formEditor.firstName + ' ' + props.formEditor.lastName;
  const [lastSavedBy, setLastSavedBy] = useState(displayEditorName);
  const [showRevertModal, setShowRevertModal] = useState<RevertModalType>(
    'closed'
  );

  const isCopyingTemplate = window.location.pathname.includes('copy');
  const [saved, setSaved] = useState(!isCopyingTemplate);

  useEffect(() => {
    if (isCopyingTemplate) {
      dirty();
    }
  }, [isCopyingTemplate, dirty]);

  useEffect(() => {
    if (isCopyingTemplate) {
      createToast(<Toast type="success">Your template has been copied</Toast>);
    }
  }, []);

  const [setSyncScrollingId, editorPanel, previewPanel] = useSyncedScrolling();
  const selectBlockId = useCallback(
    (id: string) => {
      setSyncScrollingId(id);
      updateSelectedBlockID(id);
    },
    [setSyncScrollingId]
  );

  const hasBeenPublishedBefore = props.formPublisher.date !== null;

  const title = () =>
    props.formType === 'quality_assessment'
      ? 'Quality assessment template'
      : 'Data extraction template';

  const toastFailure = (
    fallback: string,
    errors?: readonly { message: string }[] | null
  ) => {
    const message = errors?.length
      ? errors.map((error) => error.message).join(' ')
      : fallback;
    createToast(
      <Toast type="danger" role="alert">
        {message}
      </Toast>,
      { id: FAILURE_TOAST_ID }
    );
  };

  const save = () => {
    const model = toModel(viewModel);
    createRevisionMutation({
      variables: {
        extractionFormRevision: {
          extractionFormId: props.formId,
          definition: model,
          reviewId: props.reviewId,
          formType: props.formType,
          reviewerId: props.currentReviewerId,
        },
      },
    })
      .then(({ data }) => {
        const payload = data?.createExtractionFormRevision;
        if (!payload?.success) {
          toastFailure(SAVE_FAILED, payload?.errors);
          return;
        }

        dismissToast(FAILURE_TOAST_ID);
        clean();
        setPublished(false);
        setSaved(true);
        setLastSaved(new Date());
        setLastSavedBy('You');
      })
      .catch((e) => {
        MetaError.notify(e);
        toastFailure(SAVE_FAILED);
      });
  };

  const publish = (options: { force: boolean; revisitStudies?: boolean }) => {
    const showWarning =
      options.force === false &&
      (props.progress.completed > 0 || props.progress.started > 0) &&
      !!hasBeenPublishedBefore;

    if (showWarning) {
      updateShowWarningDialog(true);
      return;
    }

    const model = toModel(viewModel);
    const mutationInput: any = {
      extractionFormId: props.formId,
      definition: model,
      reviewId: props.reviewId,
      formType: props.formType,
      reviewerId: props.currentReviewerId,
    };

    if (options.revisitStudies !== undefined) {
      // User chose whether to revisit studies via modal
      mutationInput.requiresRevisit = options.revisitStudies;
    } else if (!hasBeenPublishedBefore) {
      // First publish of either template forces studies to be revisited (modal is not shown)
      mutationInput.requiresRevisit = true;
    }

    completeRevisionMutation({
      variables: {
        extractionFormRevision: mutationInput,
      },
    })
      .then(({ data }) => {
        // A rejected publish still resolves, so check the payload before redirecting away
        const payload = data?.completeExtractionForm;
        if (!payload?.success) {
          toastFailure(PUBLISH_FAILED, payload?.errors);
          return;
        }

        clean();
        const publishRoute = props.studyId
          ? props.backUrl
          : tokenizeRoute(EXTRACTION_STUDIES_LIST_ROUTE, {
              review_id: props.rawReviewId,
            }).toString();
        window.location.assign(publishRoute);
      })
      .catch((e) => {
        MetaError.notify(e);
        toastFailure(PUBLISH_FAILED);
      });
  };

  const templateToolInfo = () => {
    return (
      <TemplateToolInfo
        title={title()}
        subTitle="Extraction 2"
        icon={IconList.light.faBook}
      />
    );
  };

  const toolbarControls = () => {
    const publisher = props.formPublisher.isCurrentReviewer
      ? 'You'
      : props.formPublisher.firstName + ' ' + props.formPublisher.lastName;
    const sameRevision = lastSaved === props.formPublisher.date;

    const publishButtonText = (completing: boolean): string => {
      if (completing) {
        return 'Publishing…';
      }
      if (props.studyId) {
        return 'Publish & return to study';
      }
      return 'Publish template';
    };

    return (
      <Inline spacing={4}>
        <Inline>
          {!published && lastSaved && !sameRevision && (
            <Box ml={2}>
              {lastSavedBy} saved{' '}
              <FormattedDate date={lastSaved} format="relative" /> ago{' '}
              {props.formPublisher.date && '·'}
            </Box>
          )}
          {props.formPublisher.date && (
            <Box ml={1}>
              {publisher} published{' '}
              <FormattedDate
                date={props.formPublisher.date}
                format="relative"
              />{' '}
              ago
            </Box>
          )}
        </Inline>
        <Badge
          type={published ? 'success' : 'warning'}
          variant="solid"
          size="lg"
        >
          {published ? 'Published' : 'Draft'}
        </Badge>
        <Button
          disabled={saving || saved}
          onClick={save}
          type="brand"
          variant="outline"
        >
          {saving ? 'Saving…' : 'Save draft'}
        </Button>
        <Button
          disabled={completing || published || reverting}
          onClick={() => publish({ force: false })}
          type="brand"
        >
          {publishButtonText(completing)}
        </Button>
      </Inline>
    );
  };

  const templateChanged = useCallback(
    (updated: any) => {
      dirty();
      updateViewModel(updated);
      setPublished(false);
      setSaved(false);
    },
    [dirty]
  );

  return (
    <QueryProvider>
      <div className={styles.Build}>
        {reverting && <LoadingOverlay />}
        <RevertModal
          mode={
            (revertTemplateError && 'error') ||
            (reverting === true && 'closed') ||
            showRevertModal
          }
          onRevert={revertTemplateOnClick}
          onDismiss={() => {
            setShowRevertModal('closed');
          }}
          onCancel={() => {
            setShowRevertModal('closed');
          }}
          onSuccess={() => {
            setShowRevertModal('closed');
            clean();
            window.location.reload();
          }}
        />
        <header className={styles.headerPanel}>
          <ToolbarNav
            backUrl={props.backUrl}
            left={templateToolInfo()}
            right={toolbarControls()}
          />
        </header>
        <div className={styles.settingsPanel} data-testid="build-editor-panel">
          <Heading type="h5" className={styles.sectionHeading}>
            Item settings
          </Heading>
          <BuildSettings
            hasBeenPublishedBefore={hasBeenPublishedBefore}
            formType={props.formType}
            viewModel={viewModel}
            blockID={selectedBlockID}
            onChange={templateChanged}
          />
        </div>
        <div className={styles.editorAndPreviewPanelsContainer}>
          <div className={styles.editorPanel} ref={editorPanel}>
            <Heading type="h5" className={styles.sectionHeading}>
              Template editor
            </Heading>
            <SuggestionAutocompleteProvider
              findSuggestionOptions={findSuggestionOptions}
            >
              <BuildEditor
                viewModel={viewModel}
                onChange={templateChanged}
                formType={props.formType}
                onSelectBlock={selectBlockId}
                selectedBlockID={selectedBlockID}
                revertButtonOnClick={() => {
                  setShowRevertModal('ask');
                }}
                showRevertButton={
                  !!props.formId && props.isRevertable && !published
                }
                showReuseTemplateLink={
                  !hasBeenPublishedBefore && isDataExtractionTemplate
                }
                onReuseTemplate={() => setShowReuseModal(true)}
                showSuggestionsMessage={suggestionsEnabled}
                isFieldSuggestable={isFieldSuggestable}
              />
            </SuggestionAutocompleteProvider>
          </div>
          <div
            className={styles.previewPanel}
            ref={previewPanel}
            data-testid="build-preview-panel"
          >
            <Heading type="h5" className={styles.sectionHeading}>
              Preview template
            </Heading>
            <BuildPreview viewModel={viewModel} />
          </div>
        </div>
        <RevisitStudiesDialog
          inProgress={props.progress.started}
          completed={props.progress.completed}
          isOpen={showWarningDialog}
          formType={props.formType}
          onDismiss={() => {
            updateShowWarningDialog(false);
          }}
          onConfirm={(revisitStudies) => {
            updateShowWarningDialog(false);
            publish({ force: true, revisitStudies });
          }}
        />
        <ReuseTemplateModal
          isOpen={showReuseModal}
          onClose={() => setShowReuseModal(false)}
          reviewId={props.rawReviewId}
          reviewerId={props.currentReviewerId}
        />
      </div>
    </QueryProvider>
  );
}
